// PATH: src/components/objectiveTrials/objectiveTrialLogic.ts
/**
 * Essais d'objectifs : logique pure de l'écran. La règle d'héritage est celle
 * du serveur (l'essai l'emporte sur la matière, la matière sur le global) ;
 * seuls les champs modifiés partent dans la surcharge, pour que l'origine
 * affichée (« essai ») dise exactement ce que l'essai change.
 */
import type { PillTone } from "../ui/StatusPill";
import type {
  DraftStaleReason,
  ObjectiveTrialChapter,
  ObjectiveTrialEstimate,
  ObjectiveTrialOverride,
  ObjectiveTrialStatus,
  ResolvedObjectiveRole,
} from "../../models/domain";
import { formatUsd } from "../trials/trialLogic";

/** Plafond du serveur (CHECK de 0032) : un essai juge un réglage, il ne ré-extrait pas une matière. */
export const MAX_OBJECTIVE_TRIAL_CHAPTERS = 3;

export interface ObjectiveTrialForm {
  model: string;
  instructions: string;
}

/** Le modèle de la matière ; la consigne de l'essai part vide, l'héritée s'affiche à part (voir trials/formFromResolved). */
export const formFromResolved = (r: ResolvedObjectiveRole | undefined): ObjectiveTrialForm => ({
  model: r?.model ?? "",
  instructions: "",
});

/**
 * La surcharge : la différence entre le formulaire et le réglage effectif de
 * la matière. Un champ vide ne part pas (pour le serveur, vide = hérite) ;
 * une consigne remplie REMPLACE la consigne héritée.
 */
export const buildObjectiveOverride = (form: ObjectiveTrialForm, base: ResolvedObjectiveRole | undefined): ObjectiveTrialOverride => {
  const o: { model?: string; instructions?: string } = {};
  const model = form.model.trim();
  if (model && model !== (base?.model ?? "")) o.model = model;
  const text = form.instructions.trim();
  if (text && text !== (base?.instructions ?? "").trim()) o.instructions = text;
  return Object.keys(o).length ? { objective_extraction: o } : {};
};

export const chapterSelectionError = (count: number): string | null => {
  if (count === 0) return "Choisissez au moins un chapitre.";
  if (count > MAX_OBJECTIVE_TRIAL_CHAPTERS) return `${MAX_OBJECTIVE_TRIAL_CHAPTERS} chapitres au plus par essai.`;
  return null;
};

export interface StatusMeta {
  label: string;
  tone: PillTone;
}

export const OBJECTIVE_TRIAL_STATUS: Record<ObjectiveTrialStatus, StatusMeta> = {
  running: { label: "En cours", tone: "info" },
  done: { label: "Terminé, à décider", tone: "primary" },
  failed: { label: "Échec", tone: "error" },
  applying: { label: "Application en cours", tone: "info" },
  applied: { label: "Appliqué", tone: "success" },
  discarded: { label: "Jeté", tone: "neutral" },
};

export const objectiveTrialStatus = (status: string): StatusMeta =>
  OBJECTIVE_TRIAL_STATUS[status as ObjectiveTrialStatus] ?? { label: status, tone: "neutral" };

/** Un essai qui travaille encore : l'écran le suit. */
export const isActive = (status: ObjectiveTrialStatus) => status === "running" || status === "applying";

export const canDiscardObjectiveTrial = (status: ObjectiveTrialStatus) =>
  status === "running" || status === "done" || status === "failed";

export const STALE_LABELS: Record<DraftStaleReason, string> = {
  version_changed: "une nouvelle version du chapitre a été importée depuis l'essai",
  passages_changed: "le chapitre a été republié (ses passages ont changé) depuis l'essai",
  objectives_changed: "les objectifs servis ont changé depuis l'essai : son appariement ne vaut plus",
  extraction_in_progress: "une extraction tourne sur ce chapitre : relancez un essai une fois finie",
};

/** Une raison inconnue (serveur plus récent) reste lisible. */
export const staleLabel = (reason: string) => STALE_LABELS[reason as DraftStaleReason] ?? `non applicable (${reason})`;

/** Ce que l'essai fait à un chapitre, en trois chiffres. */
export const chapterCounts = (ch: Pick<ObjectiveTrialChapter, "objectives" | "orphans">) => ({
  reused: ch.objectives.filter((o) => o.status === "reused").length,
  created: ch.objectives.filter((o) => o.status === "new").length,
  orphaned: ch.orphans.length,
});

/**
 * Le message de confirmation de « Appliquer » : ce qui sera écrit, la
 * progression perdue, ce que coûteront les faits, et ce qui est refusé.
 */
export const applyMessage = (chapters: ObjectiveTrialChapter[], factsUsd: number | null): string => {
  const ok = chapters.filter((c) => !c.error && !c.stale && c.objectives.length > 0);
  const refused = chapters.filter((c) => !c.error && c.stale);
  const total = ok.reduce(
    (acc, c) => {
      const n = chapterCounts(c);
      return { objectives: acc.objectives + c.objectives.length, reused: acc.reused + n.reused, orphaned: acc.orphaned + n.orphaned };
    },
    { objectives: 0, reused: 0, orphaned: 0 },
  );
  const lines = [
    `${total.objectives} objectif${total.objectives > 1 ? "s" : ""} écrit${total.objectives > 1 ? "s" : ""} dans ${ok.length} chapitre${ok.length > 1 ? "s" : ""}, comme après un import : ${total.reused} repris (progression des élèves gardée).`,
    total.orphaned > 0
      ? `${total.orphaned} objectif${total.orphaned > 1 ? "s" : ""} servi${total.orphaned > 1 ? "s" : ""} sans correspondant : la progression des élèves sur ${total.orphaned > 1 ? "ces objectifs sera perdue" : "cet objectif sera perdue"}.`
      : "Aucun objectif servi n'est perdu.",
    `Les faits de ces chapitres sont ensuite ré-extraits avec les réglages de la matière : ${
      factsUsd === null ? "coût non chiffrable (mesure ou tarif manquant)" : `≈ ${formatUsd(factsUsd)}`
    }.`,
  ];
  if (refused.length) {
    lines.push(`${refused.length} chapitre${refused.length > 1 ? "s" : ""} refusé${refused.length > 1 ? "s" : ""} (rien n'y sera écrit) : ${refused.map((c) => c.title).join(", ")}.`);
  }
  return lines.join("\n");
};

/** Le coût estimé avant lancement, en une phrase. */
export const estimateSentence = (e: ObjectiveTrialEstimate): string =>
  e.estimate_usd === null
    ? `${e.lessons} leçon${e.lessons > 1 ? "s" : ""} : coût non chiffrable (aucune leçon mesurée avec ce modèle).`
    : `${e.lessons} leçon${e.lessons > 1 ? "s" : ""} × ${formatUsd(e.per_lesson_usd)} = ≈ ${formatUsd(e.estimate_usd)}` +
      (e.measured ? "" : " (ordre de grandeur : ce modèle a peu d'appels mesurés)");
